import React, { useContext, useEffect, useState } from "react";
import { ExperienceContext } from "../../contexts/ExperienceContext";
import Loader from "../UI/Loader/Loader";
import { useUserContext } from "../../contexts/UserContext";
import PreviewTemplate from "./PreviewTemplate";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import { saveTemplates } from "../../utils/APIs";
import { editTemplates } from "../../utils/APIs";
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { useContentfulContext } from "../../contexts/ContentfulContext";
import { isValidJsonString } from "../../utils/helper";

const CreateTemplate = () => {
  const { userData } = useUserContext();
  const { selectedTemplateId, rawData, setSelectedTemplateId } =
    useContext(ExperienceContext);
  const { enums, content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [templateSuccess, setTemplateSuccess] = useState("");
  const [changesDetected, setChangesDetected] = useState(false);
  const [cssCode, setCssCode] = useState("{}");
  const [htmlCode, setHtmlCode] = useState("");
  const [jsonSchemaCode, setJsonSchemaCode] = useState("{}");
  const [jsonBSchemaCode, setJsonBSchemaCode] = useState("");
  const [configJsonCode, setConfigJsonCode] = useState("[]");
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [templateType, setTemplateType] = useState(enums?.templateTypes.json_a_to_a.id);
  const [templatePlatform, setTemplatePlatform] = useState("responsive");
  const [isLandscapeSupport, setIsLandscapeSupport] = useState(true);
  const [templateSource, setTemplateSource] = useState("database");
  const [apiEndPoint, setApiEndPoint] = useState("");
  const [templateIconURL, setTemplateIconURL] = useState({ web_only: "", mobile_only: "" });
  const [templateViewURL, setTemplateViewURL] = useState({ portrait_only: "", landscape_only: "" });
  const [defaultDevice, setDefaultDevice] = useState("");
  const [templateName, setTemplateName] = useState(() => {
    if (selectedTemplateId.length > 0) {
      const newTemplate = rawData.filter((item) => {
        return item._id == selectedTemplateId;
      });
      return newTemplate[0].name;
    }
    return "";
  });

  useEffect(() => {
    if (selectedTemplateId && Object.keys(selectedTemplate).length > 0) {
      if (
        JSON.stringify(cssCode) == JSON.stringify(selectedTemplate.css) &&
        JSON.stringify(htmlCode) == JSON.stringify(selectedTemplate.html) &&
        JSON.stringify(configJsonCode) == JSON.stringify(selectedTemplate.json) &&
        JSON.stringify(jsonSchemaCode) == JSON.stringify(selectedTemplate.json) &&
        templateName == selectedTemplate.name
      ) {
        setChangesDetected(false);
      } else {
        setChangesDetected(true);
      }
    }
  }, [
    cssCode,
    htmlCode,
    configJsonCode,
    templateName,
    selectedTemplateId,
    selectedTemplate,
    jsonSchemaCode
  ]);

  useEffect(() => {
    if (selectedTemplateId.length > 0) {
      const newTemplate = rawData.filter((item) => {
        return item._id == selectedTemplateId;
      });
      setSelectedTemplate(newTemplate[0]);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(selectedTemplate).length > 0) {
      setCssCode(() => {
        return selectedTemplate?.css || "{}";
      });
      setHtmlCode(() => {
        return selectedTemplate?.html || "";
      });
      setConfigJsonCode(() => {
        return selectedTemplate?.config_json || "[]";
      });
      setJsonSchemaCode(() => {
        return selectedTemplate?.json_schema || "{}";
      });
      setTemplateType(selectedTemplate?.type || "")
      setTemplatePlatform(selectedTemplate?.platform)
      setIsLandscapeSupport(selectedTemplate?.landscape_support)
      setTemplateSource(selectedTemplate?.source || "");
      setApiEndPoint(selectedTemplate?.api_end_point || "");
      if (selectedTemplate?.default_device) {
        setDefaultDevice(selectedTemplate.default_device);
      }
    }
  }, [selectedTemplate]);

  const validateTemplateName = () => {
    if (!templateName.trim()) {
      setTemplateError(content.MANAGE_TEMPLATE_CONTENT.template_name);
      return false;
    } else {
      setTemplateError("");
      return true;
    }
  };

  const validateURL = () => {
    const trimmedURL = apiEndPoint.trim();
    try {
        new URL(trimmedURL);
        setTemplateError("");
        return true;
      } catch (e) {
        setTemplateError(content.MANAGE_TEMPLATE_CONTENT.invalid_url);
        return false;
    }
  }

  const createTemplateHandler = async () => {
    try {
      setLoading(true);

      const payload = {
        name: templateName,
        icon_url: templateIconURL,
        view_url: {
          portrait_only: templateViewURL.portrait_only,
          landscape_only: templatePlatform === enums?.templatePlatforms?.web_only?.id ? "" : templateViewURL.landscape_only,
        },
        html: htmlCode,
        css: cssCode,
        type: templateType,
        platform: templatePlatform,
        landscape_support: isLandscapeSupport,
        source: templateSource,
        api_end_point : apiEndPoint,
        config_json: configJsonCode,
        json_schema: jsonSchemaCode,
        default_device: defaultDevice,
      };

      const result = await saveTemplates(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload,
        undefined,
        {
          event_name: ANALYTICS_EVENTS.CREATE_TEMPLATE,
          metadata: { template_name: templateName, template_type: templateType, platform: templatePlatform }
        }
      );
      setSelectedTemplateId(result.data._id);
      setSelectedTemplate(result.data);
      setSubmitError("");
      setTemplateSuccess(content.MANAGE_TEMPLATE_CONTENT.create_success);
    } catch (error) {
      setSubmitError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const editTemplateHandler = async () => {
    try {
      setLoading(true);

      const payload = {
        name: templateName,
        icon_url: templateIconURL,
        view_url: {
          portrait_only: templateViewURL.portrait_only,
          landscape_only: templatePlatform === enums?.templatePlatforms?.web_only?.id ? "" : templateViewURL.landscape_only,
        },
        html: htmlCode,
        css: cssCode,
        type: templateType,
        platform: templatePlatform,
        landscape_support: isLandscapeSupport,
        source: templateSource,
        api_end_point : apiEndPoint,
        config_json: configJsonCode,
        json_schema: templateSource == enums?.templateSources.api.id ? "{}" : jsonSchemaCode,
        default_device: defaultDevice,
      };
      const result = await editTemplates(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        selectedTemplateId,
        payload,
        undefined,
        {
          event_name: ANALYTICS_EVENTS.UPDATE_TEMPLATE,
          metadata: { template_id: selectedTemplateId, template_name: templateName, template_type: templateType }
        }
      );
      setSelectedTemplate(result.data);
      setSubmitError("");
      setTemplateSuccess(content.MANAGE_TEMPLATE_CONTENT.edit_success);
    } catch (error) {
      setSubmitError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplateHandler = () => {
    const isValid = validateFields();
    if(!isValid) return;
    if (!templateError) {
      if (selectedTemplateId.length > 0) {
        if (changesDetected) {
          editTemplateHandler();
        }
      } else {
        createTemplateHandler();
      }
    }
  };

  const validateFields = () => {
    let names = [];
    if(!templateName.trim()) names.push('Name');
    if(templatePlatform?.trim() == enums?.templatePlatforms.web_only.id && !templateIconURL?.web_only.trim()) {
      names.push("Web Icon URL");
    }
    if(templatePlatform?.trim() == enums?.templatePlatforms.mobile_only.id && !templateIconURL?.mobile_only.trim()) {
      names.push("Mobile Icon URL");
    }
    if(templatePlatform == enums?.templatePlatforms.responsive.id && (!templateIconURL?.web_only.trim() || !templateIconURL?.mobile_only.trim())) {
      names.push('Web & Mobile Icons URL');
    }
    if(!templatePlatform.trim()) names.push(content.MANAGE_TEMPLATE_CONTENT.platform);
    if(!templateSource.trim()) names.push(content.MANAGE_TEMPLATE_CONTENT.source);
    if(templateSource == enums?.templateSources.api.id && !apiEndPoint.trim()) names.push('API End Point');
    if(names.length > 0) {
      const suffix = names.length > 1 ? ' are required' : ' is required';
      setSubmitError(names.join(', ') + suffix);
      return false;
    }

    const isValidJsonSchema = isValidJsonString(templateSource == enums?.templateSources.api.id ? "{}" : jsonSchemaCode);
    if(!isValidJsonSchema) {
      setSubmitError(content.MANAGE_TEMPLATE_CONTENT.invalid_json_schema);
    }

    const isValidJsonConfigJson = isValidJsonString(configJsonCode);
    if(!isValidJsonConfigJson) {
      setSubmitError(content.MANAGE_TEMPLATE_CONTENT.invalid_config_json);
    }

    if(!isValidJsonSchema || !isValidJsonConfigJson) return false;

    setSubmitError('');
    return true;
  }

  return (
    <div>
      {loading && <Loader />}
      {templateSuccess && (
        <SuccessAlert disappearTime={5000}>{templateSuccess}</SuccessAlert>
      )}
      <div className="flex flex-col w-full px-4 md:px-8"> 
        <PreviewTemplate
          templateName={templateName}
          setTemplateName={setTemplateName}
          templateIconURL={templateIconURL}
          setTemplateIconURL={setTemplateIconURL}
          templateViewURL={templateViewURL}
          setTemplateViewURL={setTemplateViewURL}
          cssCode={cssCode}
          htmlCode={htmlCode}
          setHtmlCode={setHtmlCode}
          setCssCode={setCssCode}
          configJsonCode={configJsonCode}
          setConfigJsonCode={setConfigJsonCode}
          selectedTemplate={selectedTemplate}
          validateTemplateName={validateTemplateName}
          templateError={templateError}
          submitError={submitError}
          setSubmitError={setSubmitError}
          saveTemplateHandler={saveTemplateHandler}
          selectedTemplateId={selectedTemplateId}
          jsonSchemaCode={jsonSchemaCode}
          setJsonSchemaCode={setJsonSchemaCode}
          jsonBSchemaCode={jsonBSchemaCode}
          setJsonBSchemaCode={setJsonBSchemaCode}
          templateType={templateType}
          templatePlatform={templatePlatform}
          setTemplatePlatform={setTemplatePlatform}
          isLandscapeSupport={isLandscapeSupport}
          setIsLandscapeSupport={setIsLandscapeSupport}
          templateSource={templateSource}
          setTemplateSource={setTemplateSource}
          apiEndPoint={apiEndPoint}
          setApiEndPoint={setApiEndPoint}
          validateURL={validateURL}
          isCreateTemplateFlow={true}
          setTemplateType={setTemplateType}
          defaultDevice={defaultDevice}
          setDefaultDevice={setDefaultDevice}
        />
      </div>
    </div>
  );
};

export default CreateTemplate;
